import { useEffect, useMemo } from 'react';
import { Upload, message as antdMessage } from 'antd';
import type { UploadFile } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { FormField } from './FormField';
import type { FormFieldProps } from './FormField';
import type { ControllerFieldState } from 'react-hook-form';

export type UploadFieldProps = FormFieldProps & {
    fieldState?: ControllerFieldState;
    name?: string;
    value?: File | string;
    onChange?: (value: File | undefined) => void;
    accept?: string;
    maxSizeMB?: number;
};

const acceptedImageTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

export const UploadField = ({
    fieldState,
    label = '',
    value,
    onChange,
    error,
    correct,
    showValidationIcon,
    accept = acceptedImageTypes.join(','),
    maxSizeMB = 2,
    ...props
}: UploadFieldProps) => {
    const previewUrl = useMemo(
        () => (value instanceof File ? URL.createObjectURL(value) : value),
        [value]
    );

    useEffect(() => {
        return () => {
            if (value instanceof File && previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [value, previewUrl]);

    const fileList: UploadFile[] = previewUrl
        ? [{ uid: '-1', name: 'logo', status: 'done', url: previewUrl }]
        : [];

    const handleBeforeUpload = (file: File) => {
        if (!acceptedImageTypes.includes(file.type)) {
            antdMessage.error('Unsupported file type');
            return Upload.LIST_IGNORE;
        }

        if (file.size / 1024 / 1024 > maxSizeMB) {
            antdMessage.error(`Logo must be smaller than ${maxSizeMB}MB`);
            return Upload.LIST_IGNORE;
        }

        onChange?.(file);
        return false;
    };

    const handleRemove = () => {
        onChange?.(undefined);
    };

    return (
        <FormField
            {...props}
            fieldState={fieldState}
            label={label}
            labelFor={props?.id || props.name}
            error={error}
            correct={correct}
            showValidationIcon={showValidationIcon}>
            <Upload
                id={props?.id || props.name}
                listType="picture-card"
                fileList={fileList}
                accept={accept}
                maxCount={1}
                showUploadList={{ showPreviewIcon: false }}
                beforeUpload={handleBeforeUpload}
                onRemove={handleRemove}>
                {fileList.length === 0 ? (
                    <span className="upload-trigger" tabIndex={0}>
                        <PlusOutlined />
                        <div>Upload</div>
                    </span>
                ) : null}
            </Upload>
        </FormField>
    );
};
